import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ConfirmationRdvEspaceClient } from '@components/confirmation-rdv-espace-client/models';
import {
  AppointmentEvent,
  buildAppointmentIcs,
  buildGoogleCalendarUrl,
  buildOutlookCalendarUrl,
} from '@components/confirmation-rdv-espace-client/utils';
import { environment } from '@environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { AppointmentDetailsComponent } from '@shared/components/appointment-details';
import { ButtonComponent } from '@shared/components/button';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { ClickOutsideDirective } from '@shared/directives';
import { AuthenticationService } from '@shared/services/authentication';
import { CurrentLanguageService, LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { CommonStore, FunnelStore } from '@stores';

type AccountState = 'guest' | 'member';

@Component({
  selector: 'app-confirmation-rdv-espace-client',
  imports: [
    SvgComponent,
    TitleComponent,
    AppointmentDetailsComponent,
    ButtonComponent,
    TranslatePipe,
    ClickOutsideDirective,
  ],
  templateUrl: './confirmation-rdv-espace-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationRdvEspaceClientComponent
  extends AbstractDynamicComponent<ConfirmationRdvEspaceClient>
  implements OnInit, OnDestroy
{
  private readonly router = inject(Router);
  private readonly funnelStore = inject(FunnelStore);
  readonly storeSelected = this.funnelStore.select('storeSelected');
  readonly slotSelected = this.funnelStore.select('slotSelected');
  readonly rdvId = this.funnelStore.select('rdvId');
  private readonly baseUrl = inject(CommonStore).select('baseUrl');
  private readonly logs = inject(LogsService);
  private readonly authentication = inject(AuthenticationService);
  private readonly languageService = inject(LanguageService);
  private readonly document = inject(DOCUMENT);
  readonly espaceClientUrl = environment.domains[inject(CurrentLanguageService).region.region].espaceClientUrl;
  readonly isLoginAvailable = this.authentication.isAvailable && !!this.espaceClientUrl;
  /** Unknown until the browser reads the session, so the server render never shows the wrong buttons. */
  readonly accountState = signal<AccountState | undefined>(undefined);
  readonly isCalendarMenuOpen = signal(false);
  readonly calendar = computed(() => {
    const store = this.storeSelected();
    const slot = this.slotSelected();
    if (!store || !slot) return undefined;
    const event: AppointmentEvent = {
      uid: this.rdvId()!,
      title: this.languageService.instant('COMPONENT.CONFIRMATION-RDV-ESPACE-CLIENT.CALENDAR_EVENT_TITLE', {
        store: store.name,
      }),
      start: slot.formattedSlots.startDate,
      end: slot.formattedSlots.endDate,
      location: slot.visioAppointmentSelected
        ? undefined
        : `${store.name}, ${store.streetName}, ${store.zipcode} ${store.city}`,
    };
    return { event, googleUrl: buildGoogleCalendarUrl(event), outlookUrl: buildOutlookCalendarUrl(event) };
  });

  constructor() {
    super();
    afterNextRender(() => {
      if (this.isLoginAvailable) this.accountState.set(this.authentication.hasSession() ? 'member' : 'guest');
    });
  }

  ngOnInit(): void {
    const { storeSelected, slotSelected, userInfo, rdvId } = this.funnelStore.read();
    if (!storeSelected || !slotSelected || !userInfo || !rdvId) {
      this.onClose();
    }
  }

  ngOnDestroy(): void {
    // Reset the funnel whenever the confirmation screen is left, however the user leaves it
    // (close button, header navigation, browser back, starting a new appointment), so the
    // next RDV starts from a blank form instead of pre-filling the previous booking.
    this.funnelStore.set({ validatedStep: 0, currentStep: 1 });
  }

  onClose(): void {
    // The funnel reset is handled by ngOnDestroy when this screen is torn down.
    void this.router.navigate([this.baseUrl() ?? '']);
  }

  login(signup: boolean): void {
    this.authentication.loginWithRedirect(this.espaceClientUrl!, signup).catch((error: unknown) => {
      this.logs.error('ConfirmationRdvEspaceClientComponent', 'Failed to start the Auth0 login', error);
    });
  }

  downloadIcs(event: AppointmentEvent): void {
    this.isCalendarMenuOpen.set(false);
    const ics = buildAppointmentIcs(event, new Date());
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    const link = this.document.createElement('a');
    link.href = url;
    link.download = 'socooc.ics';
    link.click();
    URL.revokeObjectURL(url);
  }
}
